/**
 * Sort-state helpers for the CRM list views.
 *
 * The representation is the `"-field"` convention both list hooks already speak
 * (leading minus = descending), so nothing between a header click and Mongo has
 * to translate a second form.
 *
 * NOT every column can carry a sort. A column is sortable only if the field is
 * STORED on the document being paged. `clientName`, `dbaName` and `source` on
 * /mids are resolved after the paginated query — the company lookup and
 * sourceResolver run on the page of results — so Mongo has nothing to order by.
 * (The `source` FILTER works only because it pre-resolves companyIds into the
 * match, which produces a set, not an order.) Those columns omit `sortKey` and
 * render without an affordance, because a control that silently does nothing is
 * worse than no control.
 */

/** `"-updatedAt"` → `{ field: "updatedAt", dir: "desc" }` */
export function parseSort(sort) {
  if (!sort || typeof sort !== "string") return { field: null, dir: "asc" };
  return sort.startsWith("-")
    ? { field: sort.slice(1), dir: "desc" }
    : { field: sort, dir: "asc" };
}

/** `{ field, dir }` → `"-updatedAt"` */
export function formatSort(field, dir) {
  if (!field) return "";
  return dir === "desc" ? `-${field}` : field;
}

/**
 * Next sort string when a header is clicked.
 *
 * Re-clicking the active column flips direction. Moving to a NEW column starts
 * at that column's natural direction rather than always ascending — a date
 * column sorted oldest-first on first click is never what anyone wanted, while
 * a name column sorted Z-A is equally wrong. Columns declare which they are via
 * `defaultSortDir`.
 */
export function toggleSort(current, col) {
  if (!col?.sortKey) return current;
  const { field, dir } = parseSort(current);
  if (field === col.sortKey) {
    return formatSort(col.sortKey, dir === "asc" ? "desc" : "asc");
  }
  return formatSort(col.sortKey, col.defaultSortDir === "desc" ? "desc" : "asc");
}

/** Is this column the one currently sorted? */
export function isSortedBy(sort, col) {
  return !!col?.sortKey && parseSort(sort).field === col.sortKey;
}
